import * as React from 'react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import IndexLayout from '../layouts';
import { NavBar } from '../components/ui';
import { BottomNavSegment } from '../components/PageSegments';
import favicon from '../content/img/icon/favicon.ico';
import theme from '../util/theme';
import { mobileSize } from '../util/variables';
import { prismicClient, RichText } from '../services';
import moment from 'moment';

type FundingBlogPostMetadata = {
  title?: string;
  summary?: string;
  heroImage?: string;
  author?: string;
};

type FundingBlogPostPageContext = {
  uid: string;
  metadata?: FundingBlogPostMetadata;
};

/**
 * Content is managed in Prismic - repeatable custom type "quick_blog_post"
 */
export default ({ pageContext }: { pageContext: FundingBlogPostPageContext }) => {
  const { uid, metadata } = pageContext;
  const [document, setDocument] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        const response = await prismicClient.getByUID('quick_blog_post', uid, {});
        if (response) {
          setDocument(response);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [uid]);

  const htmlSerializer = (type: string, element: any, _content: string, _children: React.ReactNode[], key: string) => {
    if (type === 'embed' && element?.oembed?.html) {
      return (
        <VideoEmbedWrapper key={key}>
          <div dangerouslySetInnerHTML={{ __html: element.oembed.html }} />
        </VideoEmbedWrapper>
      );
    }

    return null;
  };

  const currentUrl = `https://base10.vc/${uid}`;

  if (loading) {
    const loadingTitle = metadata?.title ? `${metadata.title} - Base10` : 'Base10';

    return (
      <IndexLayout>
        <Helmet>
          <title>{loadingTitle}</title>
          <link rel="icon" href={favicon} />
          {metadata?.summary && <meta name="description" content={metadata.summary} />}
          {metadata?.heroImage && <meta name="og:image" content={metadata.heroImage} />}
        </Helmet>
        <Main>
          <NavBar invert initiallyBlack={false} />
        </Main>
      </IndexLayout>
    );
  }

  if (!document) {
    return null;
  }

  const { data } = document;
  const title = RichText.asText(data?.title || []);
  const author = data?.author || null;
  const date = data?.date ? moment(data.date).format('MMMM D, YYYY') : null;
  const heroImage = data?.hero_image?.url;
  const videoEmbed = data?.media?.html;
  const body = data?.body;
  const eyebrow = data?.eyebrow?.[0]?.text || null;
  const summary = body?.[0]?.text?.slice(0, 160) || title;

  return (
    <IndexLayout>
      <Helmet>
        <title>{title} - Base10</title>
        <link rel="stylesheet" type="text/css" href="/global.css" />
        <link rel="icon" href={favicon} />
        <html lang="en-US" />
        <meta name="theme-color" content="#1a1a1a" />
        <link rel="canonical" href={currentUrl} />
        <meta charSet="UTF-8" />
        <meta name="og:url" content={currentUrl} />
        <meta name="og:title" content={`${title} - Base10`} />
        <meta name="og:image" content={heroImage} />
        <meta name="image" property="og:image" content={heroImage} />
        <meta name="author" content={author || 'Base10'} />
        <meta name="og:description" content={summary} />
        <meta name="description" content={summary} />
        <meta name="twitter:title" content={`${title} - Base10`} />
        <meta name="twitter:description" content={summary} />
        <meta name="twitter:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="og:type" content="article" />
      </Helmet>
      <Main>
        <NavBar invert initiallyBlack={false} />
        <ContentWrapper>
          <Article>
            <Header>
              {Boolean(eyebrow) && <Overline>{eyebrow}</Overline>}
              <Title>{title}</Title>
              {(author || date) && (
                <Meta>
                  {author && <MetaText>By {author}</MetaText>}
                  {date && <MetaText>{date}</MetaText>}
                </Meta>
              )}
            </Header>

            {heroImage && (
              <HeroImageWrapper>
                <HeroImage src={heroImage} alt={title} />
              </HeroImageWrapper>
            )}

            {videoEmbed && (
              <VideoEmbedWrapper>
                <div dangerouslySetInnerHTML={{ __html: videoEmbed }} />
              </VideoEmbedWrapper>
            )}

            {body && body.length > 0 && (
              <BodyContent>
                <RichText render={body} htmlSerializer={htmlSerializer} />
              </BodyContent>
            )}
          </Article>
        </ContentWrapper>
        <BottomNavSegment />
      </Main>
    </IndexLayout>
  );
};

const Main = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 120px;
  min-height: 100vh;
  background-color: ${theme.BACKGROUND_COLOR};
  @media ${mobileSize} {
    padding-top: 80px;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;
  padding: 0 24px 80px;
  @media ${mobileSize} {
    padding: 0 20px 60px;
  }
`;

const Article = styled.article`
  /* Standalone article - no references to other posts */
`;

const Header = styled.header`
  margin-bottom: 48px;
`;

const Overline = styled.p`
  font-family: ${theme.SECONDARY_FONT};
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: ${theme.GREY_600};
  margin: 0 0 16px 0;
`;

const Title = styled.h1`
  font-family: ${theme.PRIMARY_FONT};
  font-size: 36px;
  font-weight: 600;
  line-height: 1.3;
  letter-spacing: -0.5px;
  color: ${theme.BLACK_COLOR};
  margin: 0 0 24px 0;
  @media ${mobileSize} {
    font-size: 28px;
  }
`;

const Meta = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const MetaText = styled.span`
  font-family: ${theme.PRIMARY_FONT};
  font-size: 16px;
  color: ${theme.MEDIUM_GREY};
`;

const HeroImageWrapper = styled.div`
  margin-bottom: 48px;
  border-radius: 8px;
  overflow: hidden;
`;

const HeroImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
`;

const VideoEmbedWrapper = styled.div`
  margin: 48px 0;
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 */
  height: 0;
  overflow: hidden;

  & > div {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const BodyContent = styled.div`
  font-family: ${theme.PRIMARY_FONT};
  font-size: 18px;
  line-height: 1.7;
  color: ${theme.BLACK_COLOR};

  p {
    margin: 0 0 1.5em 0;
  }

  h2 {
    font-size: 24px;
    font-weight: 600;
    margin: 2em 0 0.75em 0;
  }

  h3 {
    font-size: 20px;
    font-weight: 600;
    margin: 1.5em 0 0.5em 0;
  }

  ul,
  ol {
    margin: 1em 0;
    padding-left: 1.5em;
  }

  li {
    margin: 0.5em 0;
  }

  a {
    color: ${theme.PRIMARY_COLOR};
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }

  blockquote {
    margin: 2em 0;
    padding-left: 1.5em;
    border-left: 4px solid ${theme.PRIMARY_COLOR};
    color: ${theme.GREY_700};
    font-style: italic;
  }
`;
